import { useLazyQuery, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PRODUCT_LOOKUP_QUERY, REGISTER_MOVEMENT_MUTATION } from './inventory.gql';

import { INVENTORY_MOVEMENT_KIND_TO_GQL } from '@/shared/graphql/enums';
import { majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const schema = z.object({
  product_id: z.string().uuid(),
  branch_id: z.string().uuid(),
  kind: z.enum(['purchase', 'adjustment', 'transfer_in', 'transfer_out', 'surprise_count']),
  quantity_delta: z.string().regex(/^-?\d+(\.\d{1,3})?$/),
  unit_cost: z
    .string()
    .trim()
    .refine((value) => value === '' || /^\d+(\.\d{1,2})?$/.test(value)),
  reason: z.string().min(1).max(255),
  lot_number: z.string().optional(),
  expiration_date: z.string().optional(),
  supplier_name: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface MovementFormDialogProps {
  branch_id: string;
  initial_product?: {
    id: string;
    code: string;
    name: string;
    is_batch_tracked?: boolean;
    is_perishable?: boolean;
  };
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface ProductLookup {
  id: string;
  code: string;
  name: string;
  stock_qty: string;
  stock_min: string;
  is_batch_tracked: boolean;
  is_perishable: boolean;
}

export function MovementFormDialog({
  branch_id,
  initial_product,
  onClose,
  onSaved,
}: MovementFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      product_id: initial_product?.id ?? '',
      branch_id,
      kind: 'purchase',
      quantity_delta: '',
      unit_cost: '',
      reason: '',
      lot_number: '',
      expiration_date: '',
      supplier_name: '',
    },
  });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedFlags, setSelectedFlags] = useState<{ is_batch_tracked: boolean; is_perishable: boolean }>({
    is_batch_tracked: initial_product?.is_batch_tracked ?? false,
    is_perishable: initial_product?.is_perishable ?? false,
  });
  const [search, setSearch] = useState(initial_product ? `${initial_product.code} — ${initial_product.name}` : '');
  const [lookupProducts, { data }] = useLazyQuery<{ products: { items: ProductLookup[] } }>(
    PRODUCT_LOOKUP_QUERY,
  );

  useEffect(() => {
    if (form.getValues('product_id')) return;
    if (search.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      void lookupProducts({ variables: { search, limit: 8 } });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search, form, lookupProducts]);

  const matches = data?.products.items ?? [];
  const [registerMovement, { loading }] = useMutation(REGISTER_MOVEMENT_MUTATION);

  const onClickPickProduct = (p: ProductLookup): void => {
    form.setValue('product_id', p.id, { shouldValidate: true });
    setSelectedFlags({ is_batch_tracked: p.is_batch_tracked, is_perishable: p.is_perishable });
    setSearch(`${p.code} — ${p.name}`);
  };

  const kind = form.watch('kind');
  const show_batch_fields =
    selectedFlags.is_batch_tracked && (kind === 'purchase' || kind === 'transfer_in');

  const onSubmitMovementForm = async (values: FormValues): Promise<void> => {
    setErrorMsg(null);
    const is_inbound = values.kind === 'purchase' || values.kind === 'transfer_in';
    if (selectedFlags.is_batch_tracked && is_inbound) {
      if (!values.lot_number?.trim()) {
        setErrorMsg(t('inventory.lot_required'));
        return;
      }
      if (selectedFlags.is_perishable && !values.expiration_date?.trim()) {
        setErrorMsg(t('inventory.expiration_required'));
        return;
      }
    }
    const unit_cost_cents = values.unit_cost ? majorStringToCents(values.unit_cost).toString() : null;
    const input: Record<string, unknown> = {
      product_id: values.product_id,
      branch_id: values.branch_id,
      kind: INVENTORY_MOVEMENT_KIND_TO_GQL[values.kind],
      quantity_delta: values.quantity_delta,
      unit_cost_cents,
      reason: values.reason,
    };
    if (values.lot_number) input.lot_number = values.lot_number;
    if (values.expiration_date) input.expiration_date = values.expiration_date;
    if (values.supplier_name) input.supplier_name = values.supplier_name;
    try {
      await registerMovement({ variables: { input } });
      await onSaved();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('expiration_date_required')) {
        setErrorMsg(t('inventory.expiration_required'));
      } else if (message.includes('lot_number_required')) {
        setErrorMsg(t('inventory.lot_required'));
      } else {
        setErrorMsg(message);
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <form
          onSubmit={(event) => {
            void form.handleSubmit(onSubmitMovementForm)(event);
          }}
          noValidate
        >
          <CardHeader>
            <CardTitle>{t('inventory.register_movement')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product_search">{t('inventory.product')}</Label>
              <Input
                id="product_search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  form.setValue('product_id', '');
                  setSelectedFlags({ is_batch_tracked: false, is_perishable: false });
                }}
                placeholder={t('inventory.product_search_placeholder')}
              />
              {matches.length > 0 && !form.getValues('product_id') ? (
                <ul className="max-h-40 overflow-auto rounded-md border border-border bg-card text-sm">
                  {matches.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => onClickPickProduct(p)}
                        className="w-full cursor-pointer px-3 py-2 text-left hover:bg-secondary/40"
                      >
                      <span className="font-mono text-xs">{p.code}</span> — {p.name}{' '}
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({p.stock_qty})
                      </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {form.formState.errors.product_id ? (
                <p className="text-sm text-destructive">{t('inventory.product_required')}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="kind">{t('inventory.movement_kind')}</Label>
                <select
                  id="kind"
                  {...form.register('kind')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="purchase">{t('inventory.kind_purchase')}</option>
                  <option value="adjustment">{t('inventory.kind_adjustment')}</option>
                  <option value="transfer_in">{t('inventory.kind_transfer_in')}</option>
                  <option value="transfer_out">{t('inventory.kind_transfer_out')}</option>
                  <option value="surprise_count">{t('inventory.kind_surprise_count')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity_delta">{t('inventory.quantity_delta')}</Label>
                <Input
                  id="quantity_delta"
                  inputMode="decimal"
                  placeholder="10.000"
                  {...form.register('quantity_delta')}
                />
                <p className="text-xs text-muted-foreground">{t('inventory.quantity_delta_hint')}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit_cost">{t('inventory.unit_cost')}</Label>
              <Input
                id="unit_cost"
                inputMode="decimal"
                placeholder="200.00"
                {...form.register('unit_cost')}
              />
              <p className="text-xs text-muted-foreground">{t('inventory.unit_cost_hint')}</p>
              {form.formState.errors.unit_cost ? (
                <p className="text-sm text-destructive">{t('inventory.unit_cost_invalid')}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">{t('inventory.reason')}</Label>
              <Input id="reason" maxLength={255} {...form.register('reason')} />
            </div>

            {show_batch_fields ? (
              <div className="space-y-4 rounded-md border border-border p-3">
                <div className="space-y-2">
                  <Label htmlFor="lot_number">{t('inventory.lot_number')}</Label>
                  <Input id="lot_number" required {...form.register('lot_number')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiration_date">{t('inventory.expiration_date')}</Label>
                  <Input
                    id="expiration_date"
                    type="date"
                    required={selectedFlags.is_perishable}
                    {...form.register('expiration_date')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supplier_name">{t('inventory.supplier_name')}</Label>
                  <Input id="supplier_name" {...form.register('supplier_name')} />
                </div>
              </div>
            ) : null}

            {errorMsg ? <p className="text-sm text-destructive">{errorMsg}</p> : null}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
